import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import AnimatedNumber from "@/components/AnimatedNumber";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, Chip, statusVariant, EmptyState } from "@/components/ui-bits";
import {
  Plus, ClipboardList, Users as UsersIcon, Clock as ClockIcon, CheckCircle2, XCircle,
  Palmtree, Thermometer, User, Wallet, MoreHorizontal, Calendar, MessageCircle, Send, X,
} from "lucide-react";
import { toast } from "sonner";
import ManagerOnlineHint from "@/components/ManagerOnlineHint";
import SuccessCheckmark from "@/components/SuccessCheckmark";

const empty = { start_date: "", end_date: "", leave_type: "vacation", reason: "" };

const SEEN_KEY = "rostr:myleave:seen:v1";
const readSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
};
const writeSeen = (set) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set])); } catch {}
};

const TABS_META = (t) => ({
  vacation: { label: t("vacation") },
  sick: { label: t("sick") },
  personal: { label: t("personal") },
  unpaid: { label: t("unpaid") },
  other: { label: t("other") },
});

const TYPE_META = {
  vacation: { Icon: Palmtree, accent: "hsl(190 80% 45%)" },
  sick:     { Icon: Thermometer, accent: "hsl(0 78% 55%)" },
  personal: { Icon: User, accent: "hsl(260 60% 55%)" },
  unpaid:   { Icon: Wallet, accent: "hsl(35 90% 50%)" },
  other:    { Icon: MoreHorizontal, accent: "hsl(220 10% 50%)" },
};

const TABS = (t) => [
  { id: "all", Icon: UsersIcon, label: t("tab_all") },
  { id: "pending", Icon: ClockIcon, label: t("tab_pending") },
  { id: "approved", Icon: CheckCircle2, label: t("tab_approved") },
  { id: "rejected", Icon: XCircle, label: t("tab_rejected") },
];

function daysInclusive(a, b) {
  if (!a || !b) return 0;
  const ms = new Date(b + "T00:00:00") - new Date(a + "T00:00:00");
  return Math.max(1, Math.round(ms / 86400000) + 1);
}
function humanDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("nl", { day: "numeric", month: "short" });
}

export default function MyLeave() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(false);
  const [seen, setSeen] = useState(() => readSeen());
  // Apple-like success checkmark shown briefly after a successful submit
  // before the modal is dismissed.
  const [successLabel, setSuccessLabel] = useState(null);

  const load = async () => setList((await api.get("/leave-requests")).data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Mark items visible in current tab as seen — clears the badge dots
  useEffect(() => {
    if (!list.length) return;
    const visible = tab === "all" ? list : list.filter((r) => r.status === tab);
    const ids = visible.map((r) => r._id).filter(Boolean);
    if (!ids.length) return;
    setSeen((prev) => {
      let changed = false;
      const next = new Set(prev);
      ids.forEach((id) => { if (!next.has(id)) { next.add(id); changed = true; } });
      if (changed) writeSeen(next);
      return changed ? next : prev;
    });
  }, [list, tab]);

  const submit = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error(t("choose_dates"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/leave-requests", form);
      await load();
      setSuccessLabel(t("ok_leave_submitted"));
      setTimeout(() => {
        setShow(false);
        setForm(empty);
        setSuccessLabel(null);
      }, 1100);
    } catch { toast.error(t("error")); }
    finally { setBusy(false); }
  };

  // Only count UNSEEN items — the "bolletjes" disappear once you've viewed the tab
  const counts = useMemo(() => {
    const unseen = list.filter((r) => !seen.has(r._id));
    return {
      all: unseen.length,
      pending: unseen.filter((r) => r.status === "pending").length,
      approved: unseen.filter((r) => r.status === "approved").length,
      rejected: unseen.filter((r) => r.status === "rejected").length,
    };
  }, [list, seen]);

  const filtered = useMemo(() => tab === "all" ? list : list.filter((r) => r.status === tab), [list, tab]);

  const stats = useMemo(() => {
    const totalDays = list
      .filter((r) => r.status === "approved")
      .reduce((sum, r) => sum + daysInclusive(r.start_date, r.end_date), 0);
    const pending = list.filter((r) => r.status === "pending").length;
    return { totalDays, pending };
  }, [list]);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="page-my-leave">
      <ManagerOnlineHint />
      <PageHeader
        eyebrow={t("my_leave")}
        title={t("leave_page_title")}
        subtitle={t("leave_page_subtitle")}
        actions={
          <button onClick={() => setShow(true)} className="btn btn-primary" data-testid="add-leave">
            <Plus size={14} />{t("request_leave")}
          </button>
        }
      />

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--success-soft))] text-[hsl(var(--success))] grid place-items-center shrink-0">
            <CheckCircle2 size={16} strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-display text-[22px] font-bold text-[hsl(var(--text-strong))] leading-none tabular-nums">
              <AnimatedNumber value={stats.totalDays} />
            </div>
            <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">{t("days_approved")}</div>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--warning-soft,45_92%_92%))] text-[hsl(45_92%_45%)] grid place-items-center shrink-0">
            <ClockIcon size={16} strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-display text-[22px] font-bold text-[hsl(var(--text-strong))] leading-none tabular-nums">
              <AnimatedNumber value={stats.pending} />
            </div>
            <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">{t("waiting_decision")}</div>
          </div>
        </div>
      </div>

      {/* Icon tabs */}
      <div className="card-glass p-1 grid grid-cols-4 gap-1 w-full" data-testid="myleave-tabs">
        {TABS(t).map((tt) => (
          <button
            key={tt.id}
            onClick={() => setTab(tt.id)}
            data-testid={`myleave-tab-${tt.id}`}
            title={tt.label} aria-label={tt.label}
            className={`relative flex items-center justify-center h-10 rounded-full transition
              ${tab === tt.id
                ? "bg-[hsl(var(--primary))] text-white shadow-[var(--shadow-sm)]"
                : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] hover:bg-[hsl(var(--muted))]"}`}
          >
            <tt.Icon size={17} strokeWidth={1.9} />
            {counts[tt.id] > 0 && (
              <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full grid place-items-center text-[10px] font-bold tabular-nums leading-none
                ${tab === tt.id ? "bg-white text-[hsl(var(--primary))]" : "bg-[hsl(var(--primary))] text-white"}`}>
                {counts[tt.id]}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="text-[12px] text-[hsl(var(--text-muted))] -mt-4 pl-1">
        {TABS(t).find((x) => x.id === tab)?.label}
      </div>

      {filtered.length === 0 && (
        <EmptyState type="sent"
          icon={ClipboardList}
          title={t("no_leave_requests")}
          hint={tab === "all" ? t("no_leave_hint_all") : t("no_leave_hint_cat")}
        />
      )}

      <div className="flex flex-col gap-3">
        {filtered.map((r) => (
          <LeaveCard key={r._id} r={r} />
        ))}
      </div>

      {/* Modal composer */}
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4"
            onClick={() => setShow(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 8 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="card p-6 md:p-8 w-full max-w-md relative overflow-hidden" data-testid="leave-modal"
            >
              {successLabel && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="absolute inset-0 z-10 grid place-items-center bg-[hsl(var(--surface))] py-10"
                  data-testid="leave-success"
                >
                  <SuccessCheckmark label={successLabel} />
                </motion.div>
              )}
              <button onClick={() => setShow(false)} className="absolute top-4 right-4 btn btn-ghost !h-8 !w-8 !p-0" aria-label={t("close")}>
                <X size={14} />
              </button>
              <div className="eyebrow mb-2">{t("new_request")}</div>
              <div className="font-display text-[24px] tracking-tight text-[hsl(var(--text-strong))] mb-4">{t("request_leave")}</div>

              {/* Type picker */}
              <div className="grid grid-cols-5 gap-2 mb-4">
                {Object.entries(TYPE_META).map(([key, meta]) => {
                  const label = TABS_META(t)[key].label;
                  return (
                    <button
                      key={key}
                      onClick={() => setForm({ ...form, leave_type: key })}
                      data-testid={`leave-type-${key}`}
                      title={label}
                      className={`flex flex-col items-center gap-1 rounded-2xl px-1 py-2 transition
                        ${form.leave_type === key ? "text-white" : "text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--muted))]"}`}
                      style={form.leave_type === key ? { background: meta.accent } : {}}
                    >
                      <meta.Icon size={16} strokeWidth={1.8} />
                      <span className="text-[10px] font-semibold truncate">{label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <label className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium text-[hsl(var(--text-muted))]">{t("from_date")}</span>
                  <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input-field" data-testid="leave-start" />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium text-[hsl(var(--text-muted))]">{t("until_date")}</span>
                  <input type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input-field" data-testid="leave-end" />
                </label>
              </div>

              {form.start_date && form.end_date && (
                <div className="text-[12px] text-[hsl(var(--text-muted))] mb-3 px-1">
                  {daysInclusive(form.start_date, form.end_date)} {daysInclusive(form.start_date, form.end_date) === 1 ? t("day_single") : t("day_plural")} {t("total_label")}
                </div>
              )}

              <label className="flex flex-col gap-1.5 mb-4">
                <span className="text-[11px] font-medium text-[hsl(var(--text-muted))]">
                  {form.leave_type === "sick" ? t("sick_admin_note_label") : t("reason_optional")}
                </span>
                {form.leave_type === "sick" ? (
                  <div
                    data-testid="sick-no-medical-help"
                    className="text-[12px] text-[hsl(var(--text-muted))] leading-relaxed rounded-xl border hairline bg-[hsl(var(--surface))] px-3 py-2.5"
                  >
                    {t("sick_no_medical_help")}
                  </div>
                ) : (
                  <textarea
                    rows={3}
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    placeholder={t("reason_placeholder")}
                    className="input-field !h-auto !py-3 leading-relaxed resize-none"
                    data-testid="leave-reason-textarea"
                  />
                )}
              </label>

              <div className="flex justify-end gap-2">
                <button onClick={() => setShow(false)} className="btn btn-ghost">{t("cancel")}</button>
                <button onClick={submit} disabled={busy} className="btn btn-primary" data-testid="leave-submit">
                  <Send size={14} /> {busy ? t("sending") : t("send_request")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LeaveCard({ r }) {
  const { t } = useI18n();
  const meta = TYPE_META[r.leave_type] || TYPE_META.other;
  const label = TABS_META(t)[r.leave_type]?.label || t("other");
  const days = daysInclusive(r.start_date, r.end_date);
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="card p-4 md:p-5 relative"
      style={{ borderLeft: `6px solid ${meta.accent}` }}
      data-testid={`my-leave-${r._id}`}
    >
      <div className="absolute top-3 right-3 md:top-4 md:right-4">
        <Chip variant={statusVariant(r.status)}>{t(r.status)}</Chip>
      </div>

      <div className="flex items-start gap-3 pr-20 md:pr-28">
        <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
             style={{ background: meta.accent + "18", color: meta.accent }}>
          <meta.Icon size={17} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))]">{label}</div>
          <div className="flex items-center gap-1.5 mt-1 text-[13px] text-[hsl(var(--text-muted))] font-medium">
            <Calendar size={12} strokeWidth={1.8} />
            <span className="tabular-nums">{humanDate(r.start_date)}</span>
            <span>→</span>
            <span className="tabular-nums">{humanDate(r.end_date)}</span>
            <span className="text-[hsl(var(--text-strong))] font-bold">· {days}d</span>
          </div>
        </div>
      </div>

      {r.reason && (
        <div className="mt-3 flex items-start gap-2 text-[13px] text-[hsl(var(--text-muted))] leading-relaxed">
          <MessageCircle size={13} strokeWidth={1.8} className="mt-0.5 shrink-0" />
          <div className="italic break-words min-w-0">"{r.reason}"</div>
        </div>
      )}

      {r.created_at && (
        <div className="mt-3 pt-3 border-t hairline text-[11px] text-[hsl(var(--text-muted))]">
          {t("requested_at")} {new Date(r.created_at).toLocaleDateString("nl", { day: "numeric", month: "short" })}
          {r.decided_at && r.status !== "pending" && (
            <> · {t("decided_at")} {new Date(r.decided_at).toLocaleDateString("nl", { day: "numeric", month: "short" })}</>
          )}
        </div>
      )}
    </motion.div>
  );
}
